import { Component, computed, ElementRef, inject, model, signal, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AvatarModule } from 'primeng/avatar';
import { FormsModule } from "@angular/forms";
import { AuthService } from '../../services/auth.service'

interface NotificationsBars {
    id: string;
    label: string;
    badge?: string | any;
}

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, AppBreadcrumb, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, RippleModule, BadgeModule, OverlayBadgeModule, AvatarModule],
    template: `
        <div class="layout-topbar">
            <div class="topbar-left">
                <a tabindex="0" #menubutton type="button" class="menu-button" (click)="onMenuButtonClick()">
                    <i class="pi pi-chevron-left"></i>
                </a>
                <img class="horizontal-logo" src="/layout/images/logo.png" alt="logo"/>
                <span class="topbar-separator"></span>
                <div app-breadcrumb></div>
                <a routerLink="/">
                    <img class="mobile-logo" src="/layout/images/logo.png"
                         alt="logo"/>
                </a>
            </div>

            <div class="topbar-right">
                <ul class="topbar-menu">
                    <li class="left-sidebar-item static sm:relative">
                    <button class="app-config-button" (click)="toggleDarkMode()">
                        <i [class]="layoutService.isDarkTheme() ? 'pi pi-sun' : 'pi pi-moon'"></i>
                    </button>
                    </li>
                    <li class="right-sidebar-item static sm:relative">
                        <a class="right-sidebar-button relative z-50" pStyleClass="@next" enterFromClass="hidden"
                           enterActiveClass="animate-scalein" leaveActiveClass="animate-fadeout" leaveToClass="hidden"
                           [hideOnOutsideClick]="true">
                            <span class="w-2 h-2 rounded-full bg-red-500 absolute top-2 right-2.5"></span>
                            <i class="pi pi-bell"></i>
                        </a>
                        <div
                            class="list-none m-0 py-4 px-4 rounded-3xl border border-surface absolute bg-surface-0 dark:bg-surface-900 overflow-hidden hidden origin-top min-w-72 sm:w-[24rem] mt-2 right-0 z-50 top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                            style="right: -50px"
                        >
                            <div class="flex items-center gap-2 justify-between">
                                <span
                                    class="text-lg font-medium text-surface-950 dark:text-surface-0">Notifications</span>
                                <button pRipple
                                        class="text-surface-700 dark:text-surface-300 text-sm font-medium">
                                    Mark all as read
                                </button>
                            </div>
                            <div class="mt-4 mb-8">
                                <p-icon-field>
                                    <p-inputicon class="pi pi-search"/>
                                    <input pInputText [(ngModel)]="notificationSearch" placeholder="Search"
                                           class="!w-full"/>
                                </p-icon-field>
                            </div>
                        </div>
                    </li>
                    <li class="profile-item static sm:relative">
                        <a class="right-sidebar-button relative z-50" pStyleClass="@next" enterFromClass="hidden"
                           enterActiveClass="animate-scalein" leaveActiveClass="animate-fadeout" leaveToClass="hidden"
                           [hideOnOutsideClick]="true">
                            <p-avatar styleClass="!w-10 !h-10">
                                <img src="/layout/images/profile.jpg"/>
                            </p-avatar>
                        </a>
                        <div
                            class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-[999] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
                        >
                            <ul class="flex flex-col gap-1">
                                <li>
                                    <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                        <i class="pi pi-user"></i>
                                        <span>Profile</span>
                                    </a>
                                </li>
                                <li>
                                    <a class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer" (click)="logout()">
                                        <i class="pi pi-power-off"></i>
                                        <span>Logout</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
            </div>
        </div>`,
    
})
export class AppTopbar {
    constructor(private authService: AuthService) { }

    logout() {
        this.authService.logout();
    }

    layoutService = inject(LayoutService);

    isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    @ViewChild('menubutton') menuButton!: ElementRef;

    notificationSearch = '';

    notificationsBars = signal<NotificationsBars[]>([
        {
            id: 'general',
            label: 'General'
        }
    ]);

    notifications = signal<any[]>([
        {
            id: 'general',
            data: [
                {
                    image: '/images/avatar/avatar-square-f-1.jpg',
                    name: 'Alice Johnson',
                    description: 'Reminder: Your subscription is about to expire in 3 days. Renew now to avoid interruption.',
                    time: '30 minutes ago',
                    read: true
                },
                {
                    image: '/images/avatar/avatar-square-m-2.jpg',
                    name: 'Michael Lee',
                    description: 'The server maintenance has been completed successfully. No further downtime is expected.',
                    time: 'Yesterday at 2:15 PM',
                    read: false
                }
            ]
        }
    ]);

    selectedNotificationBar = model(this.notificationsBars()[0].id ?? 'inbox');

    selectedNotificationsBarData = computed(() => this.notifications().find((f) => f.id === this.selectedNotificationBar()).data);

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    toggleDarkMode() {
        this.layoutService.toggleDarkModeUserAction();
    }
}
